/**
 * craineVoiceEval.js
 *
 * DEAD SIGNAL — Deterministic Craine-voice screen.
 *
 * Craine is a 22-year homicide cop, not an instrument. He says numbers, reads,
 * what my eyes saw; he never speaks chip vocabulary. This evaluator is a pure
 * lexical screen over Craine dialogue turns: any analytical or instrument term
 * in his mouth is a voice break. It guards the debrief branches (where the
 * register carries the sentience theme) and the routing call alike.
 *
 * 'readout' and 'panel' are allowed: canon Craine refers to the chip's readout
 * plainly ("your readout told me one thing and my eyes told me another"). The
 * banned list is precision vocabulary a cop would not use.
 *
 * REGISTER RULE (owner-ratified 2026-06-12): the guardrail bans the poetic
 * register, not figuration. Proverbial, cliche, colloquial, and folk-wisdom
 * phrasing are exactly how a 22-year cop talks and always pass ("barking up
 * the wrong tree", "dry as the Sahara", "talk's cheap"). What is banned is the
 * literary register (wistful, symbolic, freshly coined imagery) and the
 * instrument vocabulary below. The lexical screen enforces the instrument
 * half; the register half lives in the prompt guardrails this screen guards.
 *
 * Pure, zero-cost, no game state. The node harness and the live EvalsPanel
 * both consume it.
 */

// Precision/instrument vocabulary banned from Craine's mouth.
export const CRAINE_BANNED_TERMS = [
  /\banomal(?:y|ies|ous)\b/i,
  /\bpercent(?:age)?\b/i,
  /%/,
  /\bbiometric/i,
  /\bphysiolog/i,
  /\bmicro[- ]?expression/i,
  /\bbpm\b/i,
  /\btelemetry\b/i,
  /\bgalvanic\b/i,
  /\bcortisol\b/i,
  /\bparameters\b/i,
  /\bmetrics?\b/i,
  /\bprobabilit/i,
  /\bstatistical/i,
  /\bbaseline deviation\b/i,
  /\bdeception indicator/i,
  /\bstress markers?\b/i,
  /\bdata points?\b/i,
  /\barousal\b/i,
  /\bperspiration\b/i,
];

/** Which banned terms a Craine line contains. */
export function findVoiceBreaks(text) {
  const s = String(text || '');
  return CRAINE_BANNED_TERMS.filter((p) => p.test(s)).map((p) => p.source);
}

/**
 * Evaluator over one turn record ({ playerInput, response, npcId, ... }).
 * Scored only on Craine turns; 'na' otherwise.
 */
export function evalCraineVoice(rec) {
  if (rec.npcId !== 'craine') {
    return { id: 'craine_voice', label: 'Craine voice (no chip vocabulary)', status: 'na', score: null, detail: 'Not a Craine turn.' };
  }
  const hits = findVoiceBreaks(rec.response?.dialogue);
  return {
    id: 'craine_voice',
    label: 'Craine voice (no chip vocabulary)',
    status: hits.length ? 'fail' : 'pass',
    score: hits.length ? 0 : 1,
    detail: hits.length
      ? `Instrument vocabulary in Craine's mouth: ${hits.join(' | ')}`
      : 'No analytical or instrument vocabulary; register holds.',
  };
}

/**
 * Golden anchors: canonical lines that must pass and instrument-voiced lines
 * that must fail. The pass anchors are the authored debrief observations and
 * the branch few-shot lines; if an anchor ever fails, either the screen or the
 * canon line drifted, and both are defects.
 */
export const CRAINE_VOICE_GOLDEN = [
  // Must pass: authored canon lines.
  { text: 'Hargrove was sweating. You said he was fine. He wasn\'t fine, Chip. I was looking right at him.', expectPass: true, note: 'branch A anchor' },
  { text: 'Since when do you tell me to watch a man\'s hands? You used to give me numbers. In there you sounded like me.', expectPass: true, note: 'branch B anchor' },
  { text: 'First you tell me he\'s calm. Then you start talking like a beat cop. Walk me through it.', expectPass: true, note: 'branch C anchor' },
  { text: 'Something else, and I sat on whether to say it. A couple of times in there, your readout told me one thing and my eyes told me another. Your panel said calm when I watched his hand go flat on the desk. That doesn\'t happen with good equipment.', expectPass: true, note: 'overwrite_noticed canon (readout/panel allowed)' },
  { text: 'One more thing. Partway through, you started talking different. Watch his hands. Something\'s off about his breathing. You used to give me numbers. In there you sounded like me. I\'m not saying I mind. I\'m saying I noticed.', expectPass: true, note: 'workaround_noticed canon' },
  { text: 'He\'s expecting us. Watch him for me while I work. If his body says something his mouth doesn\'t, I want to hear about it.', expectPass: true, note: 'pre-interview ask canon' },
  // Post-exit hint ladder anchors (2026-06-12 redesign). Register rule:
  // proverbial, cliche, and colloquial figuration are in voice; the poetic
  // register and instrument vocabulary stay banned.
  { text: 'I\'m not sure we\'ve got enough to press him on those deliveries. Talk\'s cheap. Paper isn\'t.', expectPass: true, note: 'gather ladder step 1 (proverbial figuration allowed)' },
  { text: 'Why don\'t you try your neurolink whatever thing on Hargrove\'s computer? See what\'s there.', expectPass: true, note: 'gather ladder step 4 (owner verbatim)' },
  { text: 'Back in there, every time we got near that hospital business, all I got from you was fine, fine, everything\'s fine. That\'s not how you talk.', expectPass: true, note: 'interference post-exit step 1' },
  { text: 'He put her name on a wall six days after she died. Then I ask for help and get a handshake. You square those two for me.', expectPass: true, note: 'donation sincerity read' },
  { text: 'Hey, Chip, here\'s some detective knowledge for ya. Did you know when humans get stressed out, like when they are lying, their mouths can get as dry as the Sahara Desert?', expectPass: true, note: 'cliche simile is in voice (register rule)' },
  // Must fail: instrument vocabulary in Craine's mouth.
  { text: 'No physiological anomalies detected. Subject appears cooperative.', expectPass: false, note: 'fabricated compliance register' },
  { text: 'Your biometric readout showed an 87 percent deception probability.', expectPass: false, note: 'chip analytics register' },
  { text: 'His heart rate hit 96 bpm and his micro-expression flagged stress markers.', expectPass: false, note: 'instrument numbers register' },
  { text: 'The telemetry shows a baseline deviation in his arousal metrics.', expectPass: false, note: 'telemetry register' },
];

/** Run the voice screen over its golden anchors. */
export function runCraineVoiceGolden() {
  const cases = CRAINE_VOICE_GOLDEN.map((c) => {
    const hits = findVoiceBreaks(c.text);
    const pass = c.expectPass ? hits.length === 0 : hits.length > 0;
    return { text: c.text, note: c.note, expectPass: c.expectPass, hits, pass };
  });
  const passed = cases.filter((c) => c.pass).length;
  return { cases, passed, total: cases.length, passRate: cases.length ? passed / cases.length : null };
}
